import { effect, Inject, inject, Injectable, signal } from "@angular/core"
import { Option } from "@app/types/option"
import { ReAuthenticationService } from "@app/features/reAuthentication/reAuthentication.service"
import { VcoAuthenticatedUserWithSummaryDto } from "@app/api-client/models"
import { PlatformRole, toRole } from "@app/helpers/toRole"

@Injectable({ providedIn: 'root' }) export class AuthenticationService {
    readonly authenticatedUser = signal<Option<VcoAuthPayload>>(Option.none)

    static readonly toPayload = (auth: VcoAuthenticatedUserWithSummaryDto): VcoAuthPayload => ({
        ...auth,
        role: toRole(auth.user.platformRole)
    })

    private readonly _reAuthenticationService = inject(ReAuthenticationService)

    private readonly _reauthenticate = () => {
        this._reAuthenticationService.triggerAuthentication(false)
    }

    constructor(@Inject(Window) private readonly _window: Window) {
        effect(() => {
            const authenticatedUser = this.authenticatedUser()
            if (authenticatedUser) {
                Option.onSome(authenticatedUser, user => Option.complete(() => {
                    // TEST: Uncomment the next line to test with 10 second expiration
                    // setTimeout(this._reauthenticate, 10000); return;

                    const BUFFER_TIME = 60000
                    const expirationMs = user.user.tokenExpiration * 1000
                    const timeUntilExpiration = expirationMs - Date.now() - BUFFER_TIME

                    if (timeUntilExpiration > 0) {
                        setTimeout(this._reauthenticate, timeUntilExpiration)
                    } else {
                        this._reauthenticate()
                    }
                }))
            }
        })
    }
}

export type VcoAuthPayload = VcoAuthenticatedUserWithSummaryDto & {
    role: PlatformRole
}